import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'
import BookedSlotItemContent from 'src/clinic/components/ClinicManageBookingsPage/slotItem/BookedSlotItemContent'
import JoinSessionRelatedButtons from 'src/consumer/components/PublicLiveSessionPage/components/JoinSessionRelatedButtons'
import useElementDimensions from 'src/customHooks/customHooks'
import { markBookedSessionAsCompletedFunction, getImageLink, logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { DisplayFlex } from '../../../ConsumerProfilePage/ProfileLeftPannelStyle'
import {
  LSVideoPartContainer
} from '../../LiveSessionPageStyles'
import CameraPreview from './CameraPreview'
import { Box, Avatar, Menu, MenuItem, IconButton, Tooltip, Divider, Typography } from '@mui/material'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import VideocamIcon from '@mui/icons-material/Videocam'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import { useContext, useState, useEffect, useMemo } from 'react'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import { GENDER_LIST_ONBOARDING, EVENT_TYPE_CONSULTATION, EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getOrdinalSuffixForSuperScript, getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'
import dayjs from 'dayjs'
import PsychologyIcon from '@mui/icons-material/Psychology'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

const JoinSessionScreen = ({
  // Props from parent
  isMountedOnAWebView,
  navigate,
  bookedEventFinalData,
  userRole,
  auxiliaryData,
  showRejoin,
  timeElapsedInSeconds,

  // Device props
  availableCameras,
  availableMicrophones,
  cameraError,
  microphoneError,
  selectedCamera,
  selectedMicrophone,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
  setSelectedCamera,
  setSelectedMicrophone,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  tokenLoading,
  userHasAllowedAudio,
  userHasAllowedVideo,
  selectedPlatform,

  // Actions
  handleJoinRoom,
  handleSeeAllSessions,
  getProcessAndSetEventData,

  // Next events
  nextEvents,
  markCompleted,
}) => {
  const { dimensions, ref } = useElementDimensions()
  const mentorProfileContext = useContext(MentorProfileContext)
  const profileContext = useContext(ProfileContext)
  // Try to get mentor profile data from MentorProfileContext first, fallback to ProfileContext
  const mentorProfileData = mentorProfileContext?.mentorProfileData || profileContext?.teacher
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  
  // Check if user is checked-in to a room
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  
  useEffect(() => {
    const checkCheckedInStatus = () => {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      setIsCheckedIn(!!checkedInDetails)
    }
    
    // Check initially
    checkCheckedInStatus()
    
    // Listen for storage changes
    const handleStorageChange = () => {
      checkCheckedInStatus()
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    // Also check periodically in case localStorage is changed in same window
    const interval = setInterval(checkCheckedInStatus, 1000)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Get patient info for mentor view
  const prescriptionData = auxiliaryData?.metaPrescription
  const therapyReportData = auxiliaryData?.therapyReport
  const screeningReportData = auxiliaryData?.screeningReport
  const patientVisitSummary = auxiliaryData?.patientVisitSummary
  
  const patientName = prescriptionData?.patientPrefill?.name || 
                      therapyReportData?.patientName || 
                      screeningReportData?.patientName ||
                      bookedEventFinalData?.userName
  const patientDateOfBirth = prescriptionData?.patientPrefill?.vital?.dateOfBirth || 
                             therapyReportData?.patientDateOfBirth || 
                             screeningReportData?.patientDateOfBirth
  const patientGender = prescriptionData?.patientPrefill?.vital?.gender || 
                        therapyReportData?.patientGender || 
                        screeningReportData?.patientGender
  const patientNumber = patientVisitSummary?.patientNumber || 
                       prescriptionData?.patientPrefill?.patientNumber ||
                       therapyReportData?.patientNumber ||
                       screeningReportData?.patientNumber ||
                       bookedEventFinalData?.appointmentNumber
  const patientVisitNumber = bookedEventFinalData?.patientMeetingSeqNumber

  // Session type: video call or in-person
  const isVideoCall = bookedEventFinalData?.meetingSrc !== 'offline'
  const isInPerson = bookedEventFinalData?.meetingSrc === 'offline'

  // Mentor info (for showing on right side)
  const mentorName = bookedEventFinalData?.mentorName
  const mentorProfilePicId = bookedEventFinalData?.mentorProfilePicId

  // Extract summary data
  const eventType = bookedEventFinalData?.eventType
  const hasTherapyData = patientVisitSummary?.therapySessionsConducted > 0 || patientVisitSummary?.inTherapySince
  const hasConsultationData = patientVisitSummary?.referringPractitionerName

  // Format date helper
  const formatDateCompact = (date) => {
    if (!date) return null
    return dayjs(date).format('DD MMM YYYY')
  }

  const getRelativeTime = (date) => {
    if (!date) return null
    return getExactDiffFromCurrentTimeDayJs(dayjs(date), dayjs(), true)
  }

  return (
    <>
      {/* Header for webview */}
      {isMountedOnAWebView && (
        <DisplayFlex>
          <div onClick={() => navigate('/webview/my-live-sessions')}>
            <u>Back to Booked Sessions</u>
          </div>
        </DisplayFlex>
      )}

      {/* Modern Minimal Header */}
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: '#ffffff',
          zIndex: 9999,
          padding: { xs: '10px 16px', md: '12px 24px' },
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          minHeight: { xs: '56px', md: '64px' },
          borderBottomLeftRadius: { xs: '12px', md: '16px' },
          borderBottomRightRadius: { xs: '12px', md: '16px' },
        }}
      >
        {/* Left side: AUMHUM Logo + Patient Info (for mentor) or Session Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, flex: 1, minWidth: 0 }}>
          {/* AUMHUM Logo */}
          <Box
            component="img"
            src={APP_CONSTANTS.APP_LOGO_LINK}
            alt="AUMHUM"
            onError={(e) => {
              e.target.src = logo_brand
            }}
            onClick={() => {
              if (isMountedOnAWebView) {
                // Do nothing for webview
              } else {
                userRole === 'mentor' ? navigate('/mentor-home') : navigate('/')
              }
            }}
            sx={{
              height: { xs: '32px', md: '36px' },
              width: 'auto',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              borderRadius: '8px',
              '&:hover': {
                transform: 'scale(1.05)',
                opacity: 0.9,
              },
            }}
          />
          
          {userRole === 'mentor' && patientName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '8px', md: '10px' }, minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: { xs: '36px', md: '40px' },
                  height: { xs: '36px', md: '40px' },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: { xs: '14px', md: '16px' },
                  flexShrink: 0,
                }}
              >
                {patientName?.charAt(0)?.toUpperCase() || 'P'}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '15px', md: '16px' },
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {patientName}
                  </Typography>
                  {patientNumber && (
                    <Typography
                      sx={{
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 500,
                        color: '#6366f1',
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        padding: '2px 8px',
                        borderRadius: '16px',
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      #{patientNumber}
                    </Typography>
                  )}
                  {patientVisitNumber && (
                    <Typography
                      sx={{
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 500,
                        color: '#8b5cf6',
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        padding: '2px 8px',
                        borderRadius: '16px',
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      {patientVisitNumber}
                      <sup style={{ fontSize: '10px', marginLeft: '1px' }}>
                        {getOrdinalSuffixForSuperScript(patientVisitNumber)}
                      </sup>{' '}
                      Visit
                    </Typography>
                  )}
                  {/* Session Type Icon */}
                  {isVideoCall && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#10b981',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#10b981',
                        }}
                      >
                        Video
                      </Typography>
                    </Box>
                  )}
                  {isInPerson && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#f59e0b',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <PersonPinIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#f59e0b',
                        }}
                      >
                        In-Person
                      </Typography>
                    </Box>
                  )}
                </Box>
                {(patientDateOfBirth || patientGender) && (
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', md: '13px' },
                      color: '#6b7280',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mt: 0.5,
                    }}
                  >
                    {patientDateOfBirth && getAgeFromDobWithTime(patientDateOfBirth)}
                    {patientDateOfBirth && patientGender && ' / '}
                    {patientGender && GENDER_LIST_ONBOARDING?.find((item) => item.value === patientGender)?.label}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, minWidth: 0, flex: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '15px', md: '16px' },
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {bookedEventFinalData?.eventName}
                  </Typography>
                  {/* Session Type Icon */}
                  {isVideoCall && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#10b981',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#10b981',
                        }}
                      >
                        Video
                      </Typography>
                    </Box>
                  )}
                  {isInPerson && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#f59e0b',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <PersonPinIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#f59e0b',
                        }}
                      >
                        In-Person
                      </Typography>
                    </Box>
                  )}
                </Box>
                {bookedEventFinalData?.meetingSrc !== 'offline' && (
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', md: '13px' },
                      color: '#6b7280',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {dayjs?.utc(bookedEventFinalData?.startTime).local()?.format('MMM DD, YYYY')} •{' '}
                    {dayjs?.utc(bookedEventFinalData?.startTime).local()?.format('h:mma')} -{' '}
                    {dayjs?.utc(bookedEventFinalData?.endTime).local()?.format('h:mma')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Right side: Checkout Button (if checked-in) or Mentor Profile */}
        {userRole === 'mentor' && isCheckedIn ? (
          <CheckoutButtonComponent
            text="Checkout"
            styleProp={{
              fontSize: { xs: '13px', md: '14px' },
              fontWeight: 600,
              padding: { xs: '8px 16px', md: '10px 20px' },
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#dc2626',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
              },
            }}
            buttonSize="medium"
            buttonVariant="contained"
          />
        ) : userRole === 'mentor' ? (
          <Tooltip title="Account Menu" arrow>
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{
                padding: '4px',
                borderRadius: '12px',
                border: '2px solid rgba(99, 102, 241, 0.2)',
                backgroundColor: 'rgba(99, 102, 241, 0.04)',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.12)',
                  borderColor: 'rgba(99, 102, 241, 0.4)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                src={
                  mentorProfileData?.profilePicId
                    ? getImageLink(mentorProfileData.profilePicId)
                    : profileContext?.teacher?.profilePicId
                    ? getImageLink(profileContext.teacher.profilePicId)
                    : mentorProfilePicId
                    ? getImageLink(mentorProfilePicId)
                    : undefined
                }
                sx={{
                  width: { xs: 32, md: 36 },
                  height: { xs: 32, md: 36 },
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
                  background:
                    mentorProfileData?.profilePicId || profileContext?.teacher?.profilePicId || mentorProfilePicId
                      ? undefined
                      : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {mentorProfileData?.name?.charAt(0)?.toUpperCase() || 
                 profileContext?.teacher?.name?.charAt(0)?.toUpperCase() || 
                 mentorName?.charAt(0)?.toUpperCase() || 
                 'M'}
              </Avatar>
            </IconButton>
          </Tooltip>
        ) : mentorName ? (
          <Avatar
            src={mentorProfilePicId ? getImageLink(mentorProfilePicId) : undefined}
            sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              border: '2px solid rgba(99, 102, 241, 0.2)',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
              background: mentorProfilePicId ? undefined : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: { xs: '14px', md: '16px' },
            }}
          >
            {mentorName?.charAt(0)?.toUpperCase() || 'M'}
          </Avatar>
        ) : null}

        {/* Dropdown Menu - Always render when user is mentor, controlled by open state */}
        {userRole === 'mentor' && (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            disableScrollLock={false}
            MenuListProps={{
              'aria-labelledby': 'account-menu-button',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                zIndex: 10000,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              root: {
                sx: {
                  zIndex: 10000,
                },
              },
            }}
          >
            {/* User Info Section */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                backgroundColor: 'rgba(99, 102, 241, 0.02)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'rgba(15, 23, 42, 0.95)',
                  fontSize: '14px',
                  mb: 0.5,
                }}
              >
                {mentorProfileData?.name || profileContext?.teacher?.name || profileContext?.name || 'Mentor'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(100, 116, 139, 0.8)',
                  fontSize: '12px',
                }}
              >
                Mentor Portal
              </Typography>
            </Box>

            {/* Profile Menu Item */}
            <MenuItem
              onClick={() => {
                navigate('/mentor-home/profile')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(71, 85, 105, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  color: 'rgba(99, 102, 241, 0.95)',
                },
              }}
            >
              <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(99, 102, 241, 0.7)' }} />
              Profile Settings
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* Logout */}
            <MenuItem
              onClick={() => {
                logoutHandlerMentor()
                navigate('/')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(239, 68, 68, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  color: 'rgba(239, 68, 68, 1)',
                },
              }}
            >
              <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
              Sign Out
            </MenuItem>
          </Menu>
        )}
      </Box>

      {/* Main Join Area */}
      <LSVideoPartContainer
        style={{
          flexDirection: 'column',
          gap: 0,
          flexGrow: 1,
          padding: 0,
          minHeight: `calc(100vh - ${dimensions?.height}px)`,
          overflowY: 'auto',
          overflowX: 'hidden',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 50%, #f0f2f5 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '24px', md: '32px' },
            padding: { xs: '20px', md: '32px' },
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
            alignItems: 'center',
          }}
        >
          {/* Therapy/Consultation Summary - Compact Display */}
          {((eventType === EVENT_TYPE_CONSULTATION && hasTherapyData) || 
            (eventType === EVENT_TYPE_THERAPY_SESSION && hasConsultationData)) && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '800px' },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '12px', md: '16px' },
              }}
            >
              {/* Therapy Summary (shown for consultations) */}
              {eventType === EVENT_TYPE_CONSULTATION && hasTherapyData && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    borderRadius: '16px',
                    padding: { xs: '16px', md: '20px' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: '12px', md: '16px' },
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                      borderRadius: '12px',
                      padding: { xs: '10px', md: '12px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PsychologyIcon sx={{ fontSize: { xs: '20px', md: '24px' }, color: '#6366f1' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '14px', md: '15px' },
                        fontWeight: 600,
                        color: '#111827',
                        mb: { xs: '6px', md: '8px' },
                      }}
                    >
                      Therapy Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '4px', md: '6px' } }}>
                      {patientVisitSummary?.referringPractitionerName && (
                        <Typography
                          sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          In therapy with{' '}
                          <Box component="strong" sx={{ fontWeight: 600 }}>
                            {patientVisitSummary.referringPractitionerName}
                          </Box>
                        </Typography>
                      )}
                      {patientVisitSummary?.therapySessionsConducted > 0 && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          {patientVisitSummary.therapySessionsConducted} session{patientVisitSummary.therapySessionsConducted !== 1 ? 's' : ''} completed
                        </Typography>
                      )}
                      {patientVisitSummary?.inTherapySince && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          Started {formatDateCompact(patientVisitSummary.inTherapySince)} ({getRelativeTime(patientVisitSummary.inTherapySince)})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Consultation Summary (shown for therapy sessions) */}
              {eventType === EVENT_TYPE_THERAPY_SESSION && hasConsultationData && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                    borderRadius: '16px',
                    padding: { xs: '16px', md: '20px' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: '12px', md: '16px' },
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                      borderRadius: '12px',
                      padding: { xs: '10px', md: '12px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocalHospitalIcon sx={{ fontSize: { xs: '20px', md: '24px' }, color: '#10b981' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '14px', md: '15px' },
                        fontWeight: 600,
                        color: '#111827',
                        mb: { xs: '6px', md: '8px' },
                      }}
                    >
                      Consultation Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '4px', md: '6px' } }}>
                      {patientVisitSummary?.referringPractitionerName && (
                        <Typography
                          sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          Referring Doctor:{' '}
                          <Box component="strong" sx={{ fontWeight: 600 }}>
                            {patientVisitSummary.referringPractitionerName}
                          </Box>
                        </Typography>
                      )}
                      {patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor !== undefined && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          {patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor + 1}
                          <sup style={{ fontSize: '10px' }}>
                            {getOrdinalSuffixForSuperScript(patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor + 1)}
                          </sup>{' '}
                          therapy session
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Camera Preview and Controls Container - Side by side on desktop */}
          {selectedPlatform?.id === 'aumhum' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '24px', md: '32px' },
                width: '100%',
                maxWidth: '1000px',
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              {/* Camera Preview - Left side on desktop, top on mobile */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '0 0 400px' },
                  width: { xs: '100%', md: '400px' },
                }}
              >
                <CameraPreview
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  userHasAllowedVideo={userHasAllowedVideo}
                  userHasAllowedAudio={userHasAllowedAudio}
                  isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
                  meetingSrc={bookedEventFinalData?.meetingSrc}
                  microphoneError={microphoneError}
                  cameraError={cameraError}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                />
              </Box>

              {/* Join Session Controls - Right side on desktop, bottom on mobile */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '1' },
                  width: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '24px', md: '24px' },
                }}
              >
                <JoinSessionRelatedButtons
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  cameraError={cameraError}
                  handleJoinRoom={handleJoinRoom}
                  handleSeeAllSessions={handleSeeAllSessions}
                  microphoneError={microphoneError}
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  tokenLoading={tokenLoading}
                  userHasAllowedAudio={userHasAllowedAudio}
                  userHasAllowedVideo={userHasAllowedVideo}
                  isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
                  showTermsText={bookedEventFinalData?.eventType !== 'consultation'}
                  showRejoin={showRejoin}
                  bookedEventData={bookedEventFinalData}
                  auxiliaryData={auxiliaryData}
                  showPdfRelatedActions={bookedEventFinalData?.eventType === 'consultation'}
                  hideDeviceSelection={true}
                />
              </Box>
            </Box>
          ) : (
            /* For non-video call sessions, show controls normally */
            <JoinSessionRelatedButtons
              availableCameras={availableCameras}
              availableMicrophones={availableMicrophones}
              cameraError={cameraError}
              handleJoinRoom={handleJoinRoom}
              handleSeeAllSessions={handleSeeAllSessions}
              microphoneError={microphoneError}
              selectedCamera={selectedCamera}
              selectedMicrophone={selectedMicrophone}
              setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
              setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
              setSelectedCamera={setSelectedCamera}
              setSelectedMicrophone={setSelectedMicrophone}
              setUserHasAllowedAudio={setUserHasAllowedAudio}
              setUserHasAllowedVideo={setUserHasAllowedVideo}
              tokenLoading={tokenLoading}
              userHasAllowedAudio={userHasAllowedAudio}
              userHasAllowedVideo={userHasAllowedVideo}
              isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
              showTermsText={bookedEventFinalData?.eventType !== 'consultation'}
              showRejoin={showRejoin}
              bookedEventData={bookedEventFinalData}
              auxiliaryData={auxiliaryData}
              showPdfRelatedActions={bookedEventFinalData?.eventType === 'consultation'}
            />
          )}

          {/* Mark Complete Button for Mentors */}
          {/* {showRejoin && userRole === 'mentor' && !Boolean(markCompleted) && (
          <div
            onClick={async () => {
              await markBookedSessionAsCompletedFunction(bookedEventFinalData)
              await getProcessAndSetEventData()
            }}
            style={{ textAlign: 'center', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Mark Complete
          </div>
        )} */}

          {/* Next Sessions for Mentors */}
          {showRejoin && userRole === 'mentor' && nextEvents && nextEvents?.length > 0 && (
            <Box
              sx={{
                marginTop: { xs: '32px', md: '40px' },
                maxWidth: '768px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  fontSize: { xs: '18px', md: '20px' },
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '16px',
                }}
              >
                Next Sessions
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {nextEvents?.map((nextEvent, nextEventIndex) => (
                  <BookedSlotItemContent
                    onClickItem={async () => {
                      if (!Boolean(markCompleted)) {
                        await markBookedSessionAsCompletedFunction(bookedEventFinalData)
                      }
                      navigate(`/l/${nextEvent?.slug}`)
                      window.location.reload()
                    }}
                    itemContainerStyles={{ position: 'unset' }}
                    bookedSlot={nextEvent}
                    key={nextEventIndex}
                    forMentor
                    minimalUI
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </LSVideoPartContainer>
    </>
  )
}

export default JoinSessionScreen
